import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import {
  useSurveyQueryActiveBrandGroup,
  useSurveyQueryData,
  useSurveyQuerySurveyFilterTextGroups,
} from '~/hooks/useSurveyQuery'
import {
  assertExists,
  dataDisplayTypeLabelI18nKeyMap,
  type FigureInfo,
  FigureInfoHelpers,
  type Group,
  resultViewerTypeLabelI18nKeyMap,
  toGroupIndex,
} from '~/utils'

import {
  brand1IdAtom,
  brand2IdAtom,
  dataDisplayTypeAtom,
  lastTabulatedVariablesAtom,
  resultViewerTypeAtom,
} from '../atoms'
import {
  createFiveSegmentWithAllLabelMap,
  invertedOverlapKeyMap,
  type OverlapKey,
  pathKey,
} from '../utils'

export const useCreateFigureInfo = () => {
  const { t } = useTranslation()
  const survey = useSurveyQueryData()
  const brandGroup = useSurveyQueryActiveBrandGroup()

  const dataDisplayType = useAtomValue(dataDisplayTypeAtom)
  const brand1Id = useAtomValue(brand1IdAtom)
  const brand2Id = useAtomValue(brand2IdAtom)
  const resultViewerType = useAtomValue(resultViewerTypeAtom)

  const surveyFilterTextGroups = useSurveyQuerySurveyFilterTextGroups()

  const lastTabulatedVariable = useAtomValue(lastTabulatedVariablesAtom)

  const fiveSegmentWithAllLabelMap = React.useMemo(
    () => createFiveSegmentWithAllLabelMap(t),
    [t],
  )

  return React.useCallback(
    (figureType: string): FigureInfo => {
      assertExists(survey, t('assertion_failed'))
      const brandAccountId = survey.brandAccountId

      const variableOverlapGroupFilters_ =
        lastTabulatedVariable?.overlapGroupFilters
      const variableOverlapGroupFilters = R.isArray(
        variableOverlapGroupFilters_,
      )
        ? variableOverlapGroupFilters_
        : []
      const overlapGroupFilters: FigureInfo['overlapGroupFilters'] =
        variableOverlapGroupFilters.map((groupFilter) => {
          const brand1 =
            brandGroup?.brands.find((brand) => brand.id === groupFilter.brand1)
              ?.name ?? ''
          const brand2 =
            brandGroup?.brands.find((brand) => brand.id === groupFilter.brand2)
              ?.name ?? ''

          return {
            brand1,
            brand2,
            name: t('grouptogroupindexgroupfilter_group_as_group', {
              index: toGroupIndex(groupFilter.group as Group),
            }),
            combinations: groupFilter.combinations.map((combination) => {
              const [brand1Key, brand2Key] = combination.split('_x_') as [
                OverlapKey,
                OverlapKey,
              ]

              return {
                brand1:
                  fiveSegmentWithAllLabelMap[invertedOverlapKeyMap[brand1Key]],
                brand2:
                  fiveSegmentWithAllLabelMap[invertedOverlapKeyMap[brand2Key]],
                combination,
              }
            }),
          }
        })

      const questions = FigureInfoHelpers.toQuestionTexts(
        (lastTabulatedVariable?.questionIds ?? []) as string[],
        survey.questions,
      )
      const customerAttributes = FigureInfoHelpers.toCustomerAttributeTexts(
        (lastTabulatedVariable?.customerAttributeIds ?? []) as string[],
        survey.customerAttributeGroups,
        t,
      )

      return {
        brandAccountId,
        brandGroup: brandGroup?.name,
        customerAttributes,
        dataDisplayType: dataDisplayTypeLabelI18nKeyMap[dataDisplayType],
        overlapBrand1: brandGroup?.brands.find((brand) => brand.id === brand1Id)
          ?.name,
        overlapBrand2: brandGroup?.brands.find((brand) => brand.id === brand2Id)
          ?.name,
        figureType,
        overlapGroupFilters,
        pathKey,
        questions,
        resultViewerType: resultViewerTypeLabelI18nKeyMap[resultViewerType],
        survey: survey.title,
        surveyFilter: surveyFilterTextGroups,
      }
    },
    [
      brand1Id,
      brand2Id,
      brandGroup?.brands,
      brandGroup?.name,
      fiveSegmentWithAllLabelMap,
      dataDisplayType,
      lastTabulatedVariable?.customerAttributeIds,
      lastTabulatedVariable?.overlapGroupFilters,
      lastTabulatedVariable?.questionIds,
      resultViewerType,
      survey,
      surveyFilterTextGroups,
      t,
    ],
  )
}
